import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { fetchIntegrations, getCachedIntegrations, IntegrationListItem } from "../lib/catalog";
import { SectionIntro } from "../components/alt/SectionIntro";
import { ToolMarquee } from "../components/alt/ToolMarquee";
import { ArrowUp, ArrowRight, KeyRound, Check, Copy, Zap, Sparkles, Search, Globe, Plug, ShieldCheck, SquarePen, Folder, Clock, Library, Blocks, Plus, ChevronRight, ChevronLeft, Landmark, PanelLeft, Users, LayoutGrid, UserRound, Send, Share2, Contact, Linkedin, MailCheck, Layers, Flame, Video, ShieldAlert, Eye, ScanLine, Cpu, Lightbulb, Workflow, CalendarClock, BadgeCheck, PenTool, Route, LineChart, FileSearch, FileText, Bot, PhoneCall, GraduationCap, Handshake, Gift, Filter, Signature, Network, MessageCircle, Radio, Inbox, PenSquare, Headset, Database, DatabaseZap, Target, RefreshCw, Magnet, LayoutTemplate, Megaphone, BarChart3, TrendingUp, BoxIcon } from "lucide-react";
// "Giga for ChatGPT" — ALTERNATE, MARKETING cut. Positions Giga as the
// marketing operating system layered onto ChatGPT. Leads with integration
// coverage: a segmented "how many connectors" comparison, then the full
// ontology of marketing tool categories Giga covers, then how easy adding one
// is. Built on the Giga design tokens (Tailwind: brand / ink / sand / cream).

const OPENAI_ICON = 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg';
const GIGA_MARK = "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png";
const GMAIL_ICON = 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/gmail.svg';

// Segmented coverage meters (out of 10). ChatGPT alone is thin; Giga maxes it.
const METERS = [{
  metric: 'Available connectors',
  rows: [{
    who: 'ChatGPT',
    score: 3,
    brand: false
  }, {
    who: 'Giga',
    score: 10,
    brand: true
  }]
}];

// The ontology — the marketing tool categories Giga spans. Mirrors a
// "tools by category" wall; illustrative, expand from the real catalog.
const MARKETING_CATEGORIES = [{
  name: 'Email Finder',
  icon: Search
}, {
  name: 'Email Sender',
  icon: Send
}, {
  name: 'Multichannel Outreach',
  icon: Share2
}, {
  name: 'CRMs',
  icon: Contact
}, {
  name: 'LinkedIn Automation',
  icon: Linkedin
}, {
  name: 'Deliverability',
  icon: MailCheck
}, {
  name: 'Waterfall Enrichment',
  icon: Layers
}, {
  name: 'Email Warmer',
  icon: Flame
}, {
  name: 'Video Outreach',
  icon: Video
}, {
  name: 'Spam Testing',
  icon: ShieldAlert
}, {
  name: 'Visitor Identification',
  icon: Eye
}, {
  name: 'Scraping',
  icon: ScanLine
}, {
  name: 'Technology Lookup',
  icon: Cpu
}, {
  name: 'Prospect Insights',
  icon: Lightbulb
}, {
  name: 'Workflow Automation',
  icon: Workflow
}, {
  name: 'Social Scheduling',
  icon: CalendarClock
}, {
  name: 'Email Verification',
  icon: BadgeCheck
}, {
  name: 'AI Writing Assistants',
  icon: PenTool
}, {
  name: 'Attribution',
  icon: Route
}, {
  name: 'Sales Reporting',
  icon: LineChart
}, {
  name: 'Account Research',
  icon: FileSearch
}, {
  name: 'Proposals',
  icon: FileText
}, {
  name: 'AI Avatars',
  icon: Bot
}, {
  name: 'Phone Outreach',
  icon: PhoneCall
}, {
  name: 'Sales Coaching',
  icon: GraduationCap
}, {
  name: 'Warm Intros',
  icon: Handshake
}, {
  name: 'Prospect Gifting',
  icon: Gift
}, {
  name: 'Sales Funnel',
  icon: Filter
}, {
  name: 'E-Signature',
  icon: Signature
}, {
  name: 'Network Growth',
  icon: Network
}, {
  name: 'Buyer Engagement',
  icon: MessageCircle
}, {
  name: 'Social Listening',
  icon: Radio
}, {
  name: 'Inbox Management',
  icon: Inbox
}, {
  name: 'Copywriting',
  icon: PenSquare
}, {
  name: 'Customer Service',
  icon: Headset
}, {
  name: 'Prospecting Database',
  icon: Database
}, {
  name: 'Data Enrichment',
  icon: DatabaseZap
}, {
  name: 'Intent Signals',
  icon: Target
}, {
  name: 'CRM Syncing',
  icon: RefreshCw
}, {
  name: 'Lead Generation',
  icon: Magnet
}, {
  name: 'Landing Pages',
  icon: LayoutTemplate
}, {
  name: 'Ad Management',
  icon: Megaphone
}, {
  name: 'Analytics',
  icon: BarChart3
}, {
  name: 'SEO',
  icon: TrendingUp
}];

// ── "Built for teams" — the same sharing works for anything ────────
// The visual rotates a credential → file → skill; the tick marks move to
// whoever each is shared with. Spaces stay deliberately varied (a project, an
// area, an exploration).
const SHARE_TARGETS = [{
  key: 'Groups',
  icon: Users,
  people: false,
  items: ['Marketing team', 'Customer Support', 'Legal', 'Finance']
}, {
  key: 'Spaces',
  icon: LayoutGrid,
  people: false,
  items: ['Q3 Product Launch', 'Website Redesign', 'Early User Activation', 'Competitor Research', 'EMEA Expansion']
}, {
  key: 'Individuals',
  icon: UserRound,
  people: true,
  items: ['Priya Patel', 'Tom Nguyen', 'Sarah Chen', 'James Okafor']
}];

// Each rotates in at the top; `shares` lists which SHARE_TARGETS items it's
// shared with (ticked). `logo` is an image; otherwise `icon` is a lucide glyph.
const SHAREABLES = [{
  type: 'Credential',
  logo: GMAIL_ICON,
  name: 'Gmail inbox',
  sub: 'support@getgiga.com',
  shares: {
    Groups: ['Customer Support'],
    Spaces: ['Early User Activation'],
    Individuals: ['Tom Nguyen', 'Sarah Chen']
  }
}, {
  type: 'File',
  icon: FileText,
  name: '2025 Marketing Report',
  sub: 'PDF · 4.2 MB',
  shares: {
    Groups: ['Marketing team'],
    Spaces: ['Q3 Product Launch'],
    Individuals: ['Priya Patel']
  }
}, {
  type: 'Skill',
  icon: Sparkles,
  name: 'Competitor Analysis',
  sub: 'Custom skill',
  shares: {
    Groups: ['Marketing team', 'Finance'],
    Spaces: ['Competitor Research'],
    Individuals: ['James Okafor']
  }
}];

// ── Embedded expertise — Giga's structured answer vs ChatGPT's ─────
const UGC_SKILLS = [{
  initials: 'CH',
  name: 'Corey Haynes',
  skill: 'UGC growth loops',
  color: '#B0451E'
}, {
  initials: 'JP',
  name: 'Julia Pintar',
  skill: 'Creator sourcing & briefs',
  color: '#6D28D9'
}, {
  initials: 'DD',
  name: 'Dara Denney',
  skill: 'UGC ad creative',
  color: '#1E293B'
}];

// ── "Never hit a dead end" — Giga finds/builds a missing integration ──
const DEADEND_CHECKS = [{
  icon: Search,
  label: 'Checked the Giga directory',
  status: 'Not listed yet',
  tone: 'muted'
}, {
  icon: Globe,
  label: "Searched the web for Larkspur's API",
  status: 'Found',
  tone: 'green'
}, {
  icon: KeyRound,
  label: 'Detected auth — OAuth 2.0',
  status: 'Ready',
  tone: 'green'
}, {
  icon: Plug,
  label: 'Built the integration',
  status: 'Done',
  tone: 'green'
}];

// Which catalog tools ChatGPT can reach on its own (the handful that speak
// MCP / ship as first-party connectors). Illustrative — matched loosely by
// name/slug so the toggle has a believable "few lit, most dim" split.
const CHATGPT_MCP_KEYS = ['gmail', 'googledrive', 'google drive', 'googlecalendar', 'google calendar', 'github', 'slack', 'notion', 'dropbox', 'box', 'linear', 'hubspot', 'asana', 'atlassian', 'jira', 'confluence', 'canva', 'figma', 'stripe', 'intercom', 'sharepoint', 'onedrive', 'outlook', 'monday', 'zapier'];
function availableInChatGPT(item: IntegrationListItem): boolean {
  const hay = `${item.display_name} ${item.slug}`.toLowerCase();
  return CHATGPT_MCP_KEYS.some(k => hay.includes(k));
}

// The catalog API has NO popularity field and returns tools alphabetically (so
// the raw top is obscure "0codekit"-type apps). This is a curated, marketing-
// first ranking of real catalog slugs; matches float to the top in this order,
// everything else keeps the API's alphabetical order after them.
const POPULAR_SLUGS = ['hubspot', 'salesforce-rest-api', 'mailchimp', 'klaviyo-oauth', 'google-ads', 'meta-ads', 'facebook', 'linkedin', 'tiktok-com', 'stripe', 'slack', 'notion', 'intercom', 'zendesk-com', 'pipedrive-com', 'apollo', 'salesloft', 'marketo', 'attio', 'typeform-com-openapi', 'webflow', 'wordpress', 'canva', 'segment-com', 'analytics-googleapis-com', 'amplitude', 'posthog', 'sendgrid', 'github', 'linear', 'asana', 'jira', 'monday', 'clickup', 'todoist-com', 'airtable', 'zapier', 'calendly', 'zoom', 'microsoft-teams', 'google-drive', 'google-calendar', 'sheets-googleapis-com', 'googleapis-com-gmail-gmail', 'dropbox', 'sharepoint', 'confluence', 'sentry', 'discord-com', 'xero', 'freshdesk-com', 'snowflake-com-openapi'];
const POPULAR_RANK = new Map(POPULAR_SLUGS.map((s, i) => [s, i]));

// A 10-segment meter bar that fills, segment-by-segment, when it scrolls into
// view. The filled segments stagger in left→right so the bar reads as "filling
// up"; `delay` lets the Giga row start after ChatGPT's for a payoff beat.
const SEGMENT = {
  hidden: {
    opacity: 0,
    scaleX: 0.25
  },
  visible: {
    opacity: 1,
    scaleX: 1,
    transition: {
      duration: 0.34,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
function Meter({
  score,
  brand,
  delay = 0
}: {
  score: number;
  brand: boolean;
  delay?: number;
}) {
  return <motion.div className="flex gap-1" initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-12% 0px -12% 0px'
  }} transition={{
    staggerChildren: 0.07,
    delayChildren: delay
  }} data-id="element-10075" data-mp="3asvjpmmgyufs">
      {Array.from({
      length: 10
    }).map((_, i) => i < score ? <motion.div key={i} variants={SEGMENT} className={['h-2.5 flex-1 rounded-[3px] origin-left', brand ? 'bg-brand' : 'bg-ink/60'].join(' ')} data-id="element-10076" data-mp="2nuinxsyb2322" /> : <div key={i} className="h-2.5 flex-1 rounded-[3px] bg-sand-100" data-id="element-10077" data-mp="2rxexku5vaotv" />)}
    </motion.div>;
}

// Styled tooltip (no library). Renders into document.body via a portal so it's
// never clipped by the grid's scroll container, and is positioned top-center
// above the trigger using its live viewport rect. Nothing renders until hover,
// so it's SSR/prerender-safe.
function Tip({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({
      x: r.left + r.width / 2,
      y: r.top
    });
  };
  const hide = () => setPos(null);
  return <span ref={ref} onMouseEnter={show} onMouseLeave={hide} className="relative inline-flex" data-id="element-10078" data-mp="314iv020ryj6w">
      {children}
      {pos && typeof document !== 'undefined' && createPortal(<div style={{
      position: 'fixed',
      left: pos.x,
      top: pos.y,
      transform: 'translate(-50%, calc(-100% - 9px))'
    }} className="pointer-events-none z-[100] whitespace-nowrap rounded-md bg-ink text-white text-[11px] font-medium leading-none px-2 py-1.5 shadow-lg" data-id="element-10079" data-mp="29e4k8eo25mzg">
          {label}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-ink" data-id="element-10080" data-mp="3ow6evzzmctzo" />
        </div>, document.body)}
    </span>;
}

// A real integration from the Giga catalog (logo + name + first category).
// Two support marks sit in the top-right: Giga (always lit — everything works
// in Giga) and ChatGPT (faded when it can't reach the tool). Falls back to an
// initial tile when a logo is missing or fails to load.
function IntegrationTile({
  item,
  available
}: {
  item: IntegrationListItem;
  available: boolean;
}) {
  const [broken, setBroken] = useState(false);
  return <div className="relative flex items-center gap-3 p-3 rounded-xl bg-cream border border-sand-200 min-w-0" data-id="element-10081" data-mp="3l8q30qfce37m">
      {item.logo_url && !broken ? <img src={item.logo_url} alt="" loading="lazy" onError={() => setBroken(true)} className="w-8 h-8 object-contain rounded flex-none" data-id="element-10082" data-mp="3gb6pxtudrwh9" /> : <div className="w-8 h-8 rounded-lg bg-brand/10 text-brand-dark flex items-center justify-center text-sm font-bold flex-none" data-id="element-10083" data-mp="378nrdlxmo60t">{item.display_name.charAt(0).toUpperCase()}</div>}
      <div className="min-w-0 flex flex-col pr-10" data-id="element-10084" data-mp="15h26gw48qcpc">
        <span className="text-[13px] font-semibold text-ink truncate" data-id="element-10085" data-mp="15hajco0d5i0j">{item.display_name}</span>
        {item.categories[0] && <span className="text-[10px] text-ink-faint uppercase tracking-wider truncate" data-id="element-10086" data-mp="2yxez7snjc3px">{item.categories[0].name}</span>}
      </div>
      {/* Support marks — Giga always, ChatGPT faded when unsupported */}
      <div className="absolute top-2 right-2 flex items-center gap-1.5" data-id="element-10087" data-mp="2px09mh3jg2gm">
        <Tip label="Works in Giga" data-id="element-10088" data-mp="2u0y7koh4n783">
          <img src={GIGA_MARK} alt="" className="w-3.5 h-3.5 rounded-[2px]" data-id="element-10089" data-mp="3hpzluscn5cla" />
        </Tip>
        <Tip label={available ? 'Works in ChatGPT' : "Doesn't work in ChatGPT"} data-id="element-10090" data-mp="3ho6h5ill92fx">
          <img src={OPENAI_ICON} alt="" className={['w-3.5 h-3.5 transition-opacity', available ? '' : 'opacity-20 grayscale'].join(' ')} data-id="element-10091" data-mp="3moauizr79h1x" />
        </Tip>
      </div>
    </div>;
}
function MeterRow({
  who,
  score,
  brand
}: {
  who: string;
  score: number;
  brand: boolean;
}) {
  const logo = who === 'Giga' ? GIGA_MARK : OPENAI_ICON;
  return <div className="grid grid-cols-[92px_1fr] md:grid-cols-[120px_1fr] items-center gap-3 md:gap-4" data-id="element-10092" data-mp="229s5w3lrd1gp">
      <span className="inline-flex items-center gap-2 text-[13px] font-medium text-ink" data-id="element-10093" data-mp="1trp2nn00f7su">
        <img src={logo} alt="" className={['w-4 h-4', who === 'Giga' ? 'rounded-[3px]' : ''].join(' ')} data-id="element-10094" data-mp="1pqws8qfeiwuz" />
        {who}
      </span>
      <Meter score={score} brand={brand} delay={brand ? 0.35 : 0.1} data-id="element-10095" data-mp="23pcwmapskb9y" />
    </div>;
}

// Rotating "share anything" visual — a credential, then a file, then a skill,
// animating in/out at the top while the tick marks below move to whoever each
// item is shared with.
export function TeamSharingVisual() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive(i => (i + 1) % SHAREABLES.length), 3200);
    return () => clearInterval(id);
  }, []);
  const item = SHAREABLES[active];
  const shared = (key: string, label: string) => (item.shares as Record<string, string[]>)[key]?.includes(label);
  return <div className="flex flex-col items-center" data-id="element-10096" data-mp="nc61hv6ch127">
      {/* The rotating shareable */}
      <div className="flex items-center justify-center w-full h-[76px]" data-id="element-10097" data-mp="na1wtb0mfy0v">
        <AnimatePresence mode="wait" data-id="element-10098" data-mp="3mrqdflkpkqey">
          <motion.div key={item.type} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} className="inline-flex items-center gap-3 bg-white border border-sand-200 rounded-2xl shadow-[0_6px_24px_rgba(43,33,26,0.08)] pl-3 pr-3.5 py-2.5" data-id="element-10099" data-mp="3rp9qii5o6x5b">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-sand-50 border border-sand-200 flex-none" data-id="element-10100" data-mp="e92axarptm2d">
              {item.logo ? <img src={item.logo} alt="" className="w-6 h-6" data-id="element-10101" data-mp="nb1ry32qcq5b" /> : <item.icon className="w-5 h-5 text-brand-dark" data-id="element-10102" data-mp="n9qrac4kzyif" />}
            </span>
            <div className="flex flex-col text-left pr-1" data-id="element-10103" data-mp="2l1l51no4wz37">
              <span className="font-title font-bold text-[15px] leading-tight" data-id="element-10104" data-mp="2l35zldag8i12">{item.name}</span>
              <span className="font-mono text-[11.5px] text-ink-muted" data-id="element-10105" data-mp="2p482q57hle8b">{item.sub}</span>
            </div>
            <span className="inline-flex items-center bg-cream border border-sand-200 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-ink-muted flex-none" data-id="element-10106" data-mp="2cs5prrf90d8r">{item.type}</span>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Connector */}
      <div className="w-0 h-8 border-l-2 border-dashed border-[#D6CCC0]" data-id="element-10107" data-mp="23po3xe3i9mma" />

      {/* Share-with targets */}
      <div className="relative w-full border-2 border-dashed border-[#D6CCC0] rounded-[24px] bg-sand-50 pt-9 px-4 md:px-6 pb-5 md:pb-6" data-id="element-10108" data-mp="2vinezm0vgqpd">
        <div className="absolute -top-3 left-6 md:left-8 flex items-center gap-2 bg-ink text-white rounded-full py-1.5 pl-3 pr-4" data-id="element-10109" data-mp="2rg0l7fkn4vpj">
          <Share2 className="w-3.5 h-3.5 text-brand-lighter" data-id="element-10110" data-mp="2mhpbr5y9cn7u" />
          <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase" data-id="element-10111" data-mp="1g2y2oeyzdw2q">Share with</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4" data-id="element-10112" data-mp="1b18r8y7ujge4">
          {SHARE_TARGETS.map(({
          key,
          icon: Icon,
          items,
          people
        }) => <div key={key} className="bg-white border border-sand-200 rounded-2xl p-4 md:p-5 flex flex-col gap-3" data-id="element-10113" data-mp="1hi0tnfh589jb">
              <div className="flex items-center gap-2" data-id="element-10114" data-mp="13ircach7b9dr">
                <Icon className="w-4 h-4 text-brand-dark flex-none" data-id="element-10115" data-mp="17kcwyjzz8ryi" />
                <span className="font-title font-bold text-[15px]" data-id="element-10116" data-mp="17mqpom76ps2w">{key}</span>
              </div>
              <div className="flex flex-col gap-1.5 items-start" data-id="element-10117" data-mp="troblbs6owf0">
                {items.map(label => {
              const on = shared(key, label);
              return <span key={label} className={['inline-flex items-center gap-2 text-[12px] font-medium rounded-full pl-2.5 pr-2 py-1 border transition-colors duration-300', on ? 'bg-brand/[0.07] border-brand/40 text-ink' : 'bg-cream border-sand-200 text-ink'].join(' ')} data-id="element-10118" data-mp="jwcvap14nh55">
                      {people && <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-brand/10 text-brand-dark text-[8px] font-bold flex-none" data-id="element-10119" data-mp="ftzso9hofzcl">{label.split(' ').map(w => w[0]).join('').slice(0, 2)}</span>}
                      <span data-id="element-10120" data-mp="3nfj0ruxh1zv9">{label}</span>
                      <span className="inline-flex items-center justify-center w-3.5 h-3.5 flex-none" data-id="element-10121" data-mp="58mlwm1linim">
                        <AnimatePresence data-id="element-10122" data-mp="1712ehuk3azg">
                          {on && <motion.span key="t" initial={{
                      opacity: 0,
                      scale: 0.3
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} exit={{
                      opacity: 0,
                      scale: 0.3
                    }} transition={{
                      duration: 0.2
                    }} className="inline-flex" data-id="element-10123" data-mp="1685e8lefzui">
                            <Check className="w-3.5 h-3.5 text-brand-dark" strokeWidth={2.5} data-id="element-10124" data-mp="xxa1c0znyio9" />
                          </motion.span>}
                        </AnimatePresence>
                      </span>
                    </span>;
            })}
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}

// A replica of the ChatGPT composer box — reused for the prompt (with a send
// button) and for the answer (without).
function ChatBox({
  send = false,
  children
}: {
  send?: boolean;
  children: React.ReactNode;
}) {
  return <div className="w-full bg-white border border-sand-200 rounded-[26px] shadow-[0_10px_44px_rgba(43,33,26,0.09)] py-4 pl-5 pr-3 flex items-center gap-3" data-id="element-10125" data-mp="2e9crz2ucs85z">
      <div className="flex-1 text-[15px] md:text-[16px] leading-snug text-ink" data-id="element-10126" data-mp="1llqdv0ni1l4v">{children}</div>
      {send && <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#0D0D0D] text-white flex-none" data-id="element-10127" data-mp="1qktg6dc3hd95"><ArrowUp className="w-4 h-4" data-id="element-10128" data-mp="1vc37didakon5" /></span>}
    </div>;
}

// A small dashed vertical connector between the stacked flow rows.
function FlowLink() {
  return <span className="self-center w-0 h-11 border-l-2 border-dashed border-[#D6CCC0]" data-id="element-10129" data-mp="xvw2yomqq6n9" />;
}

// ChatGPT-style "tool calling" indicator — cycles through Giga's tool-call
// statuses with the Giga mark and bouncing dots, the way a chat shows work.
const GIGA_STATUSES = ['Searching the Giga directory…', 'Calling Giga tools…', 'Pulling UGC integrations…', 'Finding operator skills…'];
function GigaWorking({
  statuses = GIGA_STATUSES,
  bare = false
}: {
  statuses?: string[];
  bare?: boolean;
}) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI(x => (x + 1) % statuses.length), 1600);
    return () => clearInterval(id);
  }, [statuses]);
  const label = <AnimatePresence mode="wait" data-id="element-10130" data-mp="7ku95pd8dv23">
      <motion.span key={i} initial={{
      opacity: 0,
      y: 4
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -4
    }} transition={{
      duration: 0.2
    }} className={bare ? 'text-[14.5px] text-ink-faint whitespace-nowrap' : 'font-mono text-[11.5px] text-ink-muted whitespace-nowrap'} data-id="element-10131" data-mp="2n29uaa9b6nv">{statuses[i % statuses.length]}</motion.span>
    </AnimatePresence>;
  if (bare) return <div className="inline-flex items-center gap-2.5" data-id="element-10132" data-mp="29pqujynbzike">
      <img src={GIGA_MARK} alt="" className="w-5 h-5 rounded-[5px] animate-pulse" data-id="element-10133" data-mp="29n4ne04cr52p" />{label}
    </div>;
  return <div className="inline-flex self-start items-center gap-2 bg-cream border border-sand-200 rounded-full pl-2 pr-3 py-1.5" data-id="element-10134" data-mp="25lsvfny0a7rc">
      <img src={GIGA_MARK} alt="" className="w-4 h-4 rounded-[3px] animate-pulse" data-id="element-10135" data-mp="1vpprqdnia12a" />{label}
      <span className="flex items-center gap-0.5" data-id="element-10136" data-mp="1qq4jtdqcyidf">
        {[0, 150, 300].map(d => <span key={d} className="w-1 h-1 rounded-full bg-ink-faint animate-bounce" style={{
        animationDelay: `${d}ms`
      }} data-id="element-10137" data-mp="1qsh808mployx" />)}
      </span>
    </div>;
}

// ── Getting started — a scroll-driven recreation of the ChatGPT plugin
// install flow. Three screens rebuilt in markup (chats/recents removed), the
// left frame swapping to the active step as the steps scroll past centre.
const CAPTIONS = ['Search the ChatGPT plugin store for Giga', 'Click Install'];
const FEATURED = [{
  name: 'Data Analytics',
  desc: 'Answer product and business questions',
  icon: BarChart3,
  color: '#7C5CFC'
}, {
  name: 'GitHub',
  desc: 'Triage PRs, issues, CI, and flows',
  logo: 'github'
}, {
  name: 'Investment Banking',
  desc: 'M&A, capital markets, valuation',
  icon: Landmark,
  color: '#3A6B4E'
}, {
  name: 'Google Drive',
  desc: 'Work across Drive, Docs, Sheets',
  logo: 'googledrive'
}];
function ChatGPTSidebar() {
  const items: [string, any, boolean][] = [['New chat', SquarePen, false], ['Projects', Folder, false], ['Scheduled', Clock, false], ['Library', Library, false], ['Plugins', Blocks, true], ['Agents', Bot, false], ['GPTs', BoxIcon, false]];
  return <div className="hidden sm:flex w-[150px] md:w-[176px] flex-none flex-col gap-0.5 bg-[#F9F9F9] border-r border-black/[0.06] py-3 px-2" data-id="element-10138" data-mp="k2yxy7gd97fy">
      <div className="flex items-center justify-between px-2 pb-3" data-id="element-10139" data-mp="3nla3kqd4dtmd">
        <span className="text-[13px] font-semibold text-[#0D0D0D]" data-id="element-10140" data-mp="3nl0b8w4lradi">ChatGPT</span>
        <span className="flex items-center gap-1.5 text-[#9A9A9A]" data-id="element-10141" data-mp="3sj24g9vqsq2p"><Search className="w-3.5 h-3.5" data-id="element-10142" data-mp="3kje0ej7arave" /><PanelLeft className="w-3.5 h-3.5" data-id="element-10143" data-mp="3kim579hm3f5n" /></span>
      </div>
      {items.map(([label, Icon, active]) => <span key={label} className={['flex items-center gap-2 rounded-lg px-2 py-1.5 text-[12.5px]', active ? 'bg-black/[0.06] font-medium text-[#0D0D0D]' : 'text-[#3D3D3D]'].join(' ')} data-id="element-10144" data-mp="3fjszi38kgsg1">
          <Icon className="w-4 h-4 text-[#5C5C5C]" data-id="element-10145" data-mp="3pbf6tohsa7di" />{label}
        </span>)}
    </div>;
}
function ScreenBrowse({
  typed
}: {
  typed: string;
}) {
  return <div className="h-full overflow-hidden px-4 md:px-5 py-3.5 flex flex-col gap-3.5" data-id="element-10146" data-mp="31mmjuhj539v1">
      <div className="flex justify-center" data-id="element-10147" data-mp="2wnt4ejkal8s8">
        <div className="inline-flex items-center gap-0.5 bg-black/[0.05] rounded-full p-0.5 text-[11.5px]" data-id="element-10148" data-mp="3bjikk12sqrp1">
          <span className="px-3 py-1 rounded-full bg-white font-medium text-[#0D0D0D] shadow-sm" data-id="element-10149" data-mp="36jmnklsio0iz">Plugins</span>
          <span className="px-3 py-1 text-[#6A6A6A]" data-id="element-10150" data-mp="qrozv0kmvwtf">Skills</span>
        </div>
      </div>
      <div className="flex items-start justify-between gap-3" data-id="element-10151" data-mp="lugllb2lwcxg">
        <div className="flex flex-col" data-id="element-10152" data-mp="td6b3i1wjd5u">
          <span className="text-[20px] font-bold text-[#0D0D0D] leading-tight" data-id="element-10153" data-mp="tc4vd09xy2hs">Plugins</span>
          <span className="text-[12px] text-[#8A8A8A]" data-id="element-10154" data-mp="ydarz96q29wl">Work with ChatGPT across your favorite tools.</span>
        </div>
        <span data-cursor="search" className={['inline-flex items-center gap-1.5 rounded-full border w-[140px] overflow-hidden whitespace-nowrap px-2.5 py-1.5 text-[11px] flex-none', typed ? 'border-black/25 text-[#0D0D0D]' : 'border-black/10 text-[#9A9A9A]'].join(' ')} data-id="element-10155" data-mp="ke2q28j6cnop"><Search className="w-3 h-3 text-[#9A9A9A] flex-none" data-id="element-10156" data-mp="1h3ab7ffvsus3" />{typed ? <span data-id="element-10157" data-mp="1h4vatjh1z3wt">{typed}<span className="inline-block w-px h-[10px] bg-[#0D0D0D] align-middle ml-px animate-pulse" data-id="element-10158" data-mp="13bcant08k77j" /></span> : 'Search plugins'}</span>
      </div>
      <div className="flex flex-col gap-1.5" data-id="element-10159" data-mp="17bw9r24ddkwb">
        <span className="inline-flex items-center gap-0.5 text-[11.5px] font-semibold text-[#0D0D0D]" data-id="element-10160" data-mp="17al40wrd632k">Installed <ChevronRight className="w-3 h-3 text-[#9A9A9A]" data-id="element-10161" data-mp="1zt8jnwnlcpw9" /></span>
        <div className="flex gap-1.5" data-id="element-10162" data-mp="1zutdfk2304t2">
          <img src={GIGA_MARK} alt="" className="w-8 h-8 rounded-lg flex-none" data-id="element-10163" data-mp="c4x5rm6wmfd5" />
          {['#4285F4', '#E8710A', '#EA4335', '#F9AB00', '#0F9D58'].map((c, i) => <span key={i} className="w-8 h-8 rounded-lg border border-black/10 bg-white flex items-center justify-center flex-none" data-id="element-10164" data-mp="7497qgk9cy9f" data-mpi={i}><span className="w-3.5 h-3.5 rounded-[3px]" style={{
            background: c
          }} data-id="element-10165" data-mp="28tvft1wfaf4" /></span>)}
        </div>
      </div>
      <span className="inline-flex items-center gap-0.5 self-start bg-black/[0.05] rounded-full p-0.5 text-[11.5px]" data-id="element-10166" data-mp="uv54ls3kmjnj">
        <span className="px-2.5 py-0.5 rounded-full bg-white font-medium text-[#0D0D0D] shadow-sm" data-id="element-10167" data-mp="pw23giqpoxku">Public</span>
        <span className="px-2.5 py-0.5 text-[#6A6A6A]" data-id="element-10168" data-mp="l4ajir229q3o">Personal</span>
      </span>
      <div className="flex flex-col gap-2" data-id="element-10169" data-mp="h4tbpqz5wgvc">
        <span className="inline-flex items-center gap-0.5 text-[11.5px] font-semibold text-[#0D0D0D]" data-id="element-10170" data-mp="h1xfq86vk29j">Featured <ChevronRight className="w-3 h-3 text-[#9A9A9A]" data-id="element-10171" data-mp="1do0s04f68pfx" /></span>
        <div className="grid grid-cols-2 gap-x-5 gap-y-3" data-id="element-10172" data-mp="18ueanyy2bxds">
          {FEATURED.map(f => <div key={f.name} className="flex items-center gap-2 min-w-0" data-id="element-10173" data-mp="2ripko9gafra0">
              {f.logo ? <span className="w-7 h-7 rounded-lg border border-black/10 bg-white flex items-center justify-center flex-none" data-id="element-10174" data-mp="2wgr6h253m4p6"><img src={`https://cdn.simpleicons.org/${f.logo}`} alt="" className="w-4 h-4" data-id="element-10175" data-mp="30ico10skv75p" /></span> : <span className="w-7 h-7 rounded-lg flex items-center justify-center flex-none" style={{
            background: f.color
          }} data-id="element-10176" data-mp="35i8gc2rwxa5f"><f.icon className="w-3.5 h-3.5 text-white" data-id="element-10177" data-mp="3ag0l3xdnoqr1" /></span>}
              <div className="flex flex-col min-w-0" data-id="element-10178" data-mp="3ffd4g85bjfov">
                <span className="text-[12px] font-medium text-[#0D0D0D] leading-tight" data-id="element-10179" data-mp="3fe1yq2sbbxv4">{f.name}</span>
                <span className="text-[11px] text-[#9A9A9A] truncate" data-id="element-10180" data-mp="3k8ptgjdwtoi5">{f.desc}</span>
              </div>
              <Plus className="w-3.5 h-3.5 text-[#B0B0B0] ml-auto flex-none" data-id="element-10181" data-mp="3oaayxjo470ib" />
            </div>)}
        </div>
      </div>
    </div>;
}
function ScreenDetail({
  installHover
}: {
  installHover: boolean;
}) {
  return <div className="h-full overflow-hidden px-4 md:px-5 py-3.5 flex flex-col gap-3" data-id="element-10182" data-mp="1ynopt1kz68q">
      <span className="inline-flex items-center gap-1 text-[12px] text-[#6A6A6A]" data-id="element-10183" data-mp="1y224kru37lmc"><ChevronLeft className="w-3.5 h-3.5" data-id="element-10184" data-mp="1t6msa4bq9xs1" />Plugins</span>
      <img src={GIGA_MARK} alt="" className="w-12 h-12 rounded-xl" data-id="element-10185" data-mp="27ug73mszow9k" />
      <div className="flex items-center justify-between gap-3" data-id="element-10186" data-mp="27vrb9nqck617">
        <span className="text-[22px] font-bold text-[#0D0D0D]" data-id="element-10187" data-mp="232g2vi5vzh76">Giga</span>
        <span className="flex items-center gap-2 flex-none" data-id="element-10188" data-mp="2gtuxkm95p6u2">
          <span className="text-[#9A9A9A] text-[13px]" data-id="element-10189" data-mp="2gwh5imzyloct">•••</span>
          <span data-cursor="install" className={['inline-flex items-center bg-[#0D0D0D] text-white rounded-full px-4 py-1.5 text-[12px] font-medium origin-center transition-transform duration-200', installHover ? 'scale-[1.2] shadow-lg' : ''].join(' ')} data-id="element-10190" data-mp="2cshq67qf0vl1">Install</span>
        </span>
      </div>
      <span className="text-[12px] text-[#8A8A8A] -mt-1.5" data-id="element-10191" data-mp="wfkj3a8gdigc">Company Operating System</span>
      <div className="flex flex-col gap-1.5" data-id="element-10192" data-mp="rgq1gbm81zxt">
        <span className="text-[12.5px] font-semibold text-[#0D0D0D]" data-id="element-10193" data-mp="ri16eereldqi">App</span>
        <div className="flex items-center gap-2" data-id="element-10194" data-mp="333tijiz1cmni"><img src={GIGA_MARK} alt="" className="w-6 h-6 rounded-md" data-id="element-10195" data-mp="381v2050dinzi" /><span className="text-[12px] text-[#0D0D0D]" data-id="element-10196" data-mp="381cvmfjzytdv">Giga</span></div>
      </div>
      <div className="flex flex-col" data-id="element-10197" data-mp="2tcgoqltofmeu">
        <span className="text-[12.5px] font-semibold text-[#0D0D0D] mb-1" data-id="element-10198" data-mp="2tbdz0h83bq0i">Information</span>
        {([['Developer', 'App developer'], ['Category', 'Other'], ['Version', '1.0.0']] as [string, string][]).map(([k, v]) => <div key={k} className="flex justify-between border-b border-black/[0.06] py-1.5 text-[11.5px]" data-id="element-10199" data-mp="2pbdd6binwcjf"><span className="text-[#8A8A8A]" data-id="element-10200" data-mp="2aedzq714pph1">{k}</span><span className="text-[#0D0D0D]" data-id="element-10201" data-mp="2afnyyzjpx3p9">{v}</span></div>)}
      </div>
    </div>;
}
function ChatGPTFrame({
  step,
  typed,
  installHover
}: {
  step: number;
  typed: string;
  installHover: boolean;
}) {
  return <div className="rounded-2xl border border-sand-200 overflow-hidden bg-white shadow-[0_10px_40px_rgba(43,33,26,0.08)] flex h-[410px] md:h-[470px] text-left" data-id="element-10202" data-mp="8nizn2zw344i">
      <ChatGPTSidebar data-id="element-10203" data-mp="2ximu20f99iio" />
      <div className="flex-1 relative bg-white overflow-hidden" data-id="element-10204" data-mp="2xgtnel4n6yao">
        <AnimatePresence mode="wait" data-id="element-10205" data-mp="2sjac9u38r1my">
          <motion.div key={step} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} className="absolute inset-0 overflow-hidden" data-id="element-10206" data-mp="2skcyvptj6hx2">
            {step === 0 && <ScreenBrowse typed={typed} data-id="element-10207" data-mp="2dunmszjwgti0" />}
            {step === 1 && <ScreenDetail installHover={installHover} data-id="element-10208" data-mp="2ds1gf38qwk1d" />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}
function GetStartedFlow() {
  const [screen, setScreen] = useState(0);
  const [typed, setTyped] = useState('');
  const [installHover, setInstallHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [pointer, setPointer] = useState({
    x: 50,
    y: 58
  });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const targetRef = useRef('center');

  // Point the cursor at a real element's centre, expressed as a % of the frame
  // container — so it tracks the layout at any container width (and on resize).
  const place = (sel: string) => {
    targetRef.current = sel;
    if (sel === 'center') {
      setPointer({
        x: 50,
        y: 58
      });
      return;
    }
    const c = containerRef.current;
    const el = c?.querySelector<HTMLElement>(`[data-cursor="${sel}"]`);
    if (!c || !el) return;
    const er = el.getBoundingClientRect();
    const cr = c.getBoundingClientRect();
    setPointer({
      x: (er.left + er.width / 2 - cr.left) / cr.width * 100,
      y: (er.top + er.height / 2 - cr.top) / cr.height * 100
    });
  };

  // A looping, choreographed "screen recording": cursor → search box, type Giga,
  // results appear, cursor → Install (which grows on hover).
  useEffect(() => {
    const timers = new Set<ReturnType<typeof setTimeout>>();
    let stopped = false;
    const at = (ms: number, fn: () => void) => {
      timers.add(setTimeout(fn, ms));
    };
    const cycle = () => {
      if (stopped) return;
      timers.clear();
      setScreen(0);
      setTyped('');
      setInstallHover(false);
      setPressed(false);
      place('center');
      at(500, () => place('search'));
      at(1550, () => setPressed(true));
      at(1720, () => setPressed(false));
      at(1760, () => setTyped('G'));
      at(1890, () => setTyped('Gi'));
      at(2020, () => setTyped('Gig'));
      at(2150, () => setTyped('Giga'));
      at(2800, () => setScreen(1)); // → Giga detail
      at(3200, () => place('install'));
      at(3650, () => setInstallHover(true)); // enlarge on hover
      at(4050, () => setPressed(true));
      at(4250, () => setPressed(false));
      at(5400, cycle);
    };
    cycle();
    const onResize = () => place(targetRef.current);
    window.addEventListener('resize', onResize);
    return () => {
      stopped = true;
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  const caption = screen === 1 ? 1 : 0;
  return <div className="w-full max-w-[750px] mx-auto" data-id="element-10209" data-mp="wmmr38dm84qi">
      <div ref={containerRef} className="relative" data-id="element-10210" data-mp="sooxzugq4yhu">
        <ChatGPTFrame step={screen} typed={typed} installHover={installHover} data-id="element-10211" data-mp="16esfk84ns40y" />
        {/* Animated cursor */}
        <div className="absolute z-20 pointer-events-none transition-[left,top] duration-[850ms] ease-[cubic-bezier(0.33,0,0.2,1)]" style={{
        left: `${pointer.x}%`,
        top: `${pointer.y}%`
      }} data-id="element-10212" data-mp="11oby8cxpvy9b">
          <svg width="23" height="23" viewBox="0 0 24 24" className={['block transition-transform duration-150', pressed ? 'scale-90' : 'scale-100'].join(' ')} style={{
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))'
        }} data-id="element-10213" data-mp="1beois55j7rfg">
            <path d="M5.5 3.2 L5.5 17.6 L9.2 14.1 L11.7 19.7 L14 18.7 L11.5 13.2 L16.6 13.2 Z" fill="#fff" stroke="#1A1A1A" strokeWidth="1.1" strokeLinejoin="round" data-id="element-10214" data-mp="1kfcweyr0jprt" />
          </svg>
        </div>
        {/* Caption overlaid on the frame — bottom centre, dark pill */}
        <div className="absolute inset-x-0 bottom-4 flex justify-center px-4 pointer-events-none z-30" data-id="element-10215" data-mp="1q3tpw8xaj2t9">
          <AnimatePresence mode="wait" data-id="element-10216" data-mp="1v2xzh0o4n2k4">
            <motion.span key={caption} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.3
          }} className="inline-flex items-center gap-2.5 bg-ink text-white rounded-full pl-2 pr-4 py-1.5 text-[13px] md:text-[14px] font-medium shadow-[0_6px_20px_rgba(0,0,0,0.25)]" data-id="element-10217" data-mp="1v3ptw85zmu8t">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand text-white text-[12px] font-bold flex-none" data-id="element-10218" data-mp="2isijznzxi7vi">{caption + 1}</span>
              {CAPTIONS[caption]}
            </motion.span>
          </AnimatePresence>
        </div>
      </div>
    </div>;
}

// ── "More than answering questions" — one ad campaign carried from analyze
// → decide → coordinate → execute. Tabs advance as the section scrolls, and
// each answer is rendered the way ChatGPT actually shows it: plain text,
// bullets and a simple table, led by the "working" indicator.
const CHAIN = [{
  verb: 'Analyze',
  icon: BarChart3,
  prompt: 'hey! can you analyze our Q3 ad campaign performance?',
  kind: 'analyze',
  leadIn: "I'll pull the last 7 days across your four ad sets, then flag what's dragging the account down.",
  statuses: ['Analyzing Meta Ads Performance', 'Reviewing the last 7 days']
}, {
  verb: 'Decide',
  icon: Lightbulb,
  prompt: 'which ad sets should we cut, and where should the budget go?',
  kind: 'decide',
  leadIn: 'Let me weigh each ad set against your ROAS and CPA targets and give you a clear call.',
  statuses: ['Comparing to Your Targets', 'Weighing the options']
}, {
  verb: 'Coordinate',
  icon: Users,
  prompt: 'can you let the team know in Slack?',
  kind: 'coordinate',
  leadIn: "I'll drop a clear summary in #growth so nobody's caught off guard.",
  statuses: ['Posting to #growth', 'Notifying the team']
}, {
  verb: 'Execute',
  icon: Zap,
  prompt: 'go ahead — pause 2 & 3 and scale up 1 & 4',
  kind: 'execute',
  leadIn: "On it — I'll make the changes directly in Meta Ads Manager.",
  statuses: ['Updating Meta Ads Manager', 'Applying changes']
}];
function BeatContent({
  b,
  working
}: {
  b: (typeof CHAIN)[number];
  working: boolean;
}) {
  const rows = [{
    name: 'Ad set 1 · Broad',
    roas: '4.2×',
    cpa: '$18',
    good: true
  }, {
    name: 'Ad set 2 · Lookalike',
    roas: '1.1×',
    cpa: '$72',
    good: false
  }, {
    name: 'Ad set 3 · Interests',
    roas: '0.9×',
    cpa: '$88',
    good: false
  }, {
    name: 'Ad set 4 · Retargeting',
    roas: '3.8×',
    cpa: '$21',
    good: true
  }];
  return <div className="flex flex-col gap-3.5 text-[15px] leading-[1.6] text-ink" data-id="element-10219" data-mp="24ypub359lpuq">
      {/* User prompt — right-aligned grey bubble */}
      <div className="self-end max-w-[90%] bg-[#F4F4F4] rounded-[24px] px-5 py-3 text-[#0D0D0D]" data-id="element-10220" data-mp="29r973xw4ogyh">
        <span className="inline-flex items-center gap-1.5 mr-2 align-[-5px]" data-id="element-10221" data-mp="2j2ym8j7cjv8n">
          <img src={GIGA_MARK} alt="" className="w-[22px] h-[22px] rounded-[6px]" data-id="element-10222" data-mp="2f1v8c28v09a2" />
          <strong className="font-semibold" data-id="element-10223" data-mp="2sukt63cnj85l">Giga</strong>
        </span>
        {b.prompt}
      </div>
      <span className="self-end -mt-1.5 pr-0.5 text-ink-faint" data-id="element-10224" data-mp="2nuyedph6djuo"><Copy className="w-[18px] h-[18px]" data-id="element-10225" data-mp="2nxkmbq7za1df" /></span>
      {/* Assistant answer — left-aligned */}
      <p data-id="element-10226" data-mp="207qdrkuoyyyz">{b.leadIn}</p>
      {working ? <GigaWorking statuses={b.statuses} bare data-id="element-10227" data-mp="1w4k74c3o35rz" /> : <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.25
    }} className="flex flex-col gap-3.5" data-id="element-10228" data-mp="1w5n0cqogpkb2">
            {b.kind === 'analyze' && <>
              <div className="overflow-hidden rounded-md border border-sand-200 text-[13.5px]" data-id="element-10229" data-mp="256thn1yxqz5d">
                <div className="grid grid-cols-[1.7fr_0.7fr_0.7fr] bg-cream border-b border-sand-200 text-[11px] font-semibold text-ink-muted" data-id="element-10230" data-mp="3lh5mvaglnwcb">
                  <span className="px-2.5 py-1.5" data-id="element-10231" data-mp="3ol4p0r5jzik5">Ad set</span><span className="px-2.5 py-1.5 text-right" data-id="element-10232" data-mp="3okbow913nraz">ROAS</span><span className="px-2.5 py-1.5 text-right" data-id="element-10233" data-mp="3slnggk3jamlt">CPA</span>
                </div>
                {rows.map(r => <div key={r.name} className="grid grid-cols-[1.7fr_0.7fr_0.7fr] border-b border-sand-100 last:border-0" data-id="element-10234" data-mp="1fm1w1kd1avf">
                    <span className="px-2.5 py-1.5 text-ink truncate" data-id="element-10235" data-mp="6dnnou967oal">{r.name}</span>
                    <span className={['px-2.5 py-1.5 text-right', r.good ? 'text-ink' : 'text-red-600 font-semibold'].join(' ')} data-id="element-10236" data-mp="34xnhiww2jpw5">{r.roas}</span>
                    <span className="px-2.5 py-1.5 text-right text-ink-muted" data-id="element-10237" data-mp="39tm7uq0yjhz7">{r.cpa}</span>
                  </div>)}
              </div>
              <p data-id="element-10238" data-mp="3jm0ffzepvita">Ad sets 2 and 3 are well under your 2× ROAS target — worth a closer look.</p>
            </>}
            {b.kind === 'decide' && <ul className="list-disc pl-5 flex flex-col gap-1" data-id="element-10239" data-mp="2m5bgqel2cwo4">
                <li data-id="element-10240" data-mp="2r4o02pcq7lly">Pause <strong data-id="element-10241" data-mp="2r4fn6xglsgar">ad set 2</strong> — 1.1× ROAS, $72 CPA</li>
                <li data-id="element-10242" data-mp="2jloimp8ts458">Pause <strong data-id="element-10243" data-mp="2jletf3vlu10l">ad set 3</strong> — 0.9× ROAS, $88 CPA</li>
                <li data-id="element-10244" data-mp="2jjlvqe2yyr4q">Reallocate that budget to ad sets 1 &amp; 4, your top performers</li>
              </ul>}
            {b.kind === 'coordinate' && <blockquote className="border-l-2 border-sand-300 pl-3 text-ink-muted" data-id="element-10245" data-mp="29n8saohmgyws">Heads up team — pausing ad sets 2 &amp; 3 (ROAS under 1×) and moving budget to 1 &amp; 4. Full breakdown in the thread.</blockquote>}
            {b.kind === 'execute' && <ul className="list-disc pl-5 flex flex-col gap-1" data-id="element-10246" data-mp="20nlxwmnb8td8">
                <li data-id="element-10247" data-mp="1vvkfsk3go8fa">Paused ad set 2</li>
                <li data-id="element-10248" data-mp="1vuj7gndty019">Paused ad set 3</li>
                <li data-id="element-10249" data-mp="1qx0w6qccb8q3">Increased ad set 1 budget by 40%</li>
                <li data-id="element-10250" data-mp="1qxivxwy7o52r">Increased ad set 4 budget by 40%</li>
              </ul>}
          </motion.div>}
    </div>;
}
export function MoreThanAnswers({
  embedded = false
}: {
  embedded?: boolean;
} = {}) {
  const [active, setActive] = useState(0);
  const [working, setWorking] = useState(true);
  const [nonce, setNonce] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useInView(ref, {
    amount: 0.4
  });
  // Jump to a tab from a click, bumping `nonce` so the auto-advance + working
  // timers restart in full — even when re-clicking the tab that's showing.
  const goTo = (i: number) => {
    setActive(i);
    setNonce(n => n + 1);
  };
  // Start from the first tab whenever the section scrolls into view.
  useEffect(() => {
    if (inView) setActive(0);
  }, [inView]);
  // Auto-advance every 3s while in view; changing tab or clicking restarts it.
  useEffect(() => {
    if (!inView) return;
    const id = setTimeout(() => setActive(a => (a + 1) % CHAIN.length), 3000);
    return () => clearTimeout(id);
  }, [active, inView, nonce]);
  // The "working" indicator plays briefly on each tab, then settles into the
  // finished answer.
  useEffect(() => {
    setWorking(true);
    const id = setTimeout(() => setWorking(false), 1600);
    return () => clearTimeout(id);
  }, [active, nonce]);
  const b = CHAIN[active];
  return <div ref={ref} className={['flex flex-col items-center', embedded ? 'gap-0' : 'gap-6 md:gap-8'].join(' ')} data-id="element-10251" data-mp="1kh3kcxk15jll">
      {!embedded && <div className="flex flex-col items-center text-center gap-4" data-id="element-10252" data-mp="1fkd4obc50q0p">
          <h2 className="font-title font-extrabold text-4xl md:text-[54px] leading-[1.02] tracking-tight max-w-[18ch]" data-id="element-10253" data-mp="1titkqsvw3svi">More than just answering questions</h2>
          <p className="text-base md:text-lg leading-relaxed text-ink-muted max-w-[52ch]" data-id="element-10254" data-mp="1oltg8meouy6i">Most AI stops at an answer. Giga takes action and gets the work done.</p>
          <div className="inline-flex items-center gap-1 bg-cream border border-sand-200 rounded-full p-1 mt-1" data-id="element-10255" data-mp="1ygv4zh51xy8k">
            {CHAIN.map(({
          verb,
          icon: Icon
        }, i) => <button key={verb} type="button" onClick={() => goTo(i)} className={['inline-flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-full text-[13px] md:text-[15px] font-semibold transition-colors duration-300', i === active ? 'bg-ink text-white' : 'text-ink-muted hover:text-ink'].join(' ')} data-id="element-10256" data-mp="288ipsz73sh12" data-mpi={i}><Icon className="w-4 h-4" data-id="element-10257" data-mp="1ej2q9xgq5m5y" />{verb}</button>)}
          </div>
        </div>}
      <div className={['w-full bg-white border border-sand-200 rounded-2xl p-5 md:p-7 shadow-[0_6px_28px_rgba(43,33,26,0.06)]', embedded ? 'max-w-none min-h-[430px]' : 'max-w-[640px] min-h-[400px]'].join(' ')} data-id="element-10258" data-mp="1ai97fii52xjx">
        <AnimatePresence mode="wait" data-id="element-10259" data-mp="1ji5nivqgqo3d">
          <motion.div key={active} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.25
        }} data-id="element-10260" data-mp="1jh4esxwx6dot">
            <BeatContent b={b} working={working} data-id="element-10261" data-mp="278i4sx7llx6m" />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}

// ── "Put it down anywhere" — Giga is its own system, reachable from any
// provider, so nothing is locked in.
const HANDOFF_TARGETS = ['Slack', 'Claude Code', 'your other tools'];
const PROVIDERS = [{
  name: 'ChatGPT',
  logo: OPENAI_ICON
}, {
  name: 'Slack',
  logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg'
}, {
  name: 'Claude',
  logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/anthropic.svg'
}];
const SYSTEM_BOXES = [{
  icon: Users,
  name: 'Team',
  desc: 'Users, groups and spaces.'
}, {
  icon: Folder,
  name: 'Projects',
  desc: 'Ongoing work, kept together.'
}, {
  icon: Plug,
  name: 'Connectors',
  desc: 'All your tools, connected once.'
}, {
  icon: Sparkles,
  name: 'Skills',
  desc: 'Reusable know-how, applied on demand.'
}, {
  icon: FileText,
  name: 'Files',
  desc: 'Docs, decks and exports, understood.'
}, {
  icon: Database,
  name: 'Memories',
  desc: 'Shared, inspectable company knowledge.'
}];

// The tool name at the end of the headline, rotating on a loop.
function RotatingWord() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI(x => (x + 1) % HANDOFF_TARGETS.length), 2200);
    return () => clearInterval(id);
  }, []);
  return <span className="text-brand whitespace-nowrap" data-id="element-10262" data-mp="12y8mamm57i3f">
      <AnimatePresence mode="wait" data-id="element-10263" data-mp="1301sk0sug0aw">
        <motion.span key={i} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1]
      }} className="inline-block" data-id="element-10264" data-mp="xzdskpmn094j">{HANDOFF_TARGETS[i]}</motion.span>
      </AnimatePresence>
    </span>;
}
/**
 * The recipe every panel on this page shares.
 *
 * Was `rounded-[28px]` with no shadow — a 28px radius reads as a soft app tile
 * rather than the 22px card the rest of the site uses, and a white card with
 * no shadow on a cream ground has nothing but a hairline holding it off the
 * page. The two-part shadow (1px contact edge + a wide soft cast) is the same
 * one the home page board uses.
 */
const CARD = 'flex flex-col rounded-[22px] border border-sand-200 bg-white p-6 md:p-12 shadow-[0_1px_2px_rgba(43,33,26,0.04),0_24px_60px_-40px_rgba(43,33,26,0.35)]';
export function GigaForChatGPT2() {
  // Real integrations from the Giga catalog (same source as the /integrations
  // page). Seed from the prerender prime cache when present, else fetch on
  // mount. Purely display filler for the right columns — no filtering wired up.
  const primed = getCachedIntegrations();
  const [integrations, setIntegrations] = useState<IntegrationListItem[]>(primed ?? []);
  useEffect(() => {
    if (primed && primed.length) return;
    let cancelled = false;
    fetchIntegrations().then(rows => {
      if (!cancelled) setIntegrations(rows);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // No popularity field on the catalog, so curate: known tools to the top (in
  // POPULAR_SLUGS order), the rest keep the API's alphabetical order behind them.
  const ranked = [...integrations].sort((a, b) => (POPULAR_RANK.get(a.slug) ?? Infinity) - (POPULAR_RANK.get(b.slug) ?? Infinity));
  const shown = ranked.slice(0, 60);
  return <div className="w-full bg-cream text-ink font-body" data-id="element-10265" data-mp="30wquiv81uz2x">
      {/* ── Hero ────────────────────────────────────────────────
       Rebuilt to the home page's opening: left-aligned display type on
       textured cream with the terminating stop in orange, rather than a
       centred block of text with a letterspaced mono label floating above it.
       The lockup replaces that label — a Giga × ChatGPT pairing states the
       subject as a fact instead of announcing it in small caps. */}
      <section className="relative isolate overflow-hidden border-b border-[#E7E0D4] pb-14 pt-28 md:pb-20 md:pt-36" data-id="element-10266" data-mp="2e0qo3zrz3kca">
        <div className="dot-matrix pointer-events-none opacity-[0.35]" aria-hidden="true" data-id="element-10267" data-mp="2mzc2diqewd79" />
        <div className="relative mx-auto max-w-[1120px] px-6 md:px-10" data-id="element-10268" data-mp="2n1op09747bqn">
          <SectionIntro align="left" className="max-w-3xl text-left" data-id="element-10269" data-mp="76d2fpps1gye">
            <div className="flex items-center gap-2.5" data-id="element-10270" data-mp="281tdh7b3ah8">
              <img src={GIGA_MARK} alt="" className="size-7 rounded-[7px]" data-id="element-10271" data-mp="1k6pzfvyeov0g" />
              <span className="font-title text-[17px] font-bold tracking-tight text-ink" data-id="element-10272" data-mp="1k8ue98xufefi">Giga</span>
              <span className="text-[15px] text-[#B3A493]" data-id="element-10273" data-mp="1p3q6a932y1sm">×</span>
              <img src={OPENAI_ICON} alt="" className="size-7" data-id="element-10274" data-mp="1b736zznnmzjj" />
              <span className="font-title text-[17px] font-bold tracking-tight text-ink" data-id="element-10275" data-mp="1g4uzaus7ono9">ChatGPT</span>
            </div>
            <h1 className="font-title mt-6 text-[40px] font-bold leading-[1.04] tracking-[-0.03em] text-ink md:text-[64px]" data-id="element-10276" data-mp="23ugz9kger6xb">
              The marketing operating system inside ChatGPT<span className="text-brand" data-id="element-10277" data-mp="1u4xhyj8cfl4k">.</span>
            </h1>
            <p className="mt-5 max-w-[58ch] text-pretty text-lg leading-relaxed text-ink-muted" data-id="element-10278" data-mp="1u4npmoztt1vp">
              Every marketing tool your team touches — connected to the chat you already have open.
            </p>
          </SectionIntro>
        </div>
      </section>

      {/* ── The directory, shown rather than claimed ──────────────
       An ink band directly under the hero. It does two jobs: it is the page's
       first piece of evidence (the tools it reaches, moving), and it breaks a
       run of eight cream-and-white sections that made the page read as one
       long undifferentiated column of cards. */}
      <section className="relative isolate overflow-hidden bg-[#1A120C] py-10 md:py-12" data-id="element-10279" data-mp="2rjxbrqj6onxg">
        <div className="dot-matrix pointer-events-none opacity-[0.07]" aria-hidden="true" data-id="element-10280" data-mp="2ml3vjqciiitl" />
        <div className="relative" data-id="element-10281" data-mp="2ijinqjeu4uq9">
          <ToolMarquee data-id="element-10282" data-mp="2dqz9dk8bpvke" />
        </div>
      </section>

      <div className="max-w-[1120px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-20 md:pb-28 flex flex-col gap-10 md:gap-14" data-id="element-10283" data-mp="2dpeetum0ecmj">

        {/* ── Connect all your tools ────────────────────────────── */}
        <section className={`${CARD} gap-10 md:gap-12`} data-id="element-10284" data-mp="23ucqv23gzglj">
          {/* NO MONO EYEBROW. Six letterspaced all-caps labels down one page is
           the loudest generic tell on it — each one announced a section whose
           headline already said the same thing in the display face. */}
          <SectionIntro align="left" className="flex max-w-[60ch] flex-col gap-3 text-left" data-id="element-10285" data-mp="a99ev1t596rw">
            <h2 className="font-title text-3xl font-bold leading-[1.05] tracking-[-0.03em] md:text-[42px]" data-id="element-10286" data-mp="ea31lruuofj7">
              Connect <span className="text-brand" data-id="element-10287" data-mp="jba9rs0wngqe">all</span> your tools, not just MCP<span className="text-brand" data-id="element-10288" data-mp="o9twhunbowjt">.</span>
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-10289" data-mp="3mr7qkjtknmxp">
              ChatGPT connects to the handful of tools that speak MCP. Giga connects to{' '}
              <strong className="text-brand font-semibold" data-id="element-10290" data-mp="3rny16rmlxqbq">literally everything else</strong>.
            </p>
          </SectionIntro>

          {/* A · Segmented coverage meters */}
          <div className="rounded-2xl border border-sand-200 bg-cream p-6 md:p-8 flex flex-col gap-7" data-id="element-10291" data-mp="5c0wp17zx2qn">
            {METERS.map((group, gi) => <div key={group.metric} className={['flex flex-col gap-4', gi > 0 ? 'pt-7 border-t border-sand-200' : ''].join(' ')} data-id="element-10292" data-mp="38zt0xu55snho" data-mpi={gi}>
                <span className="font-mono text-[11px] tracking-[0.12em] uppercase text-ink-faint" data-id="element-10293" data-mp="22dibi5d2qpde">{group.metric}</span>
                <div className="flex flex-col gap-3.5" data-id="element-10294" data-mp="1xeyp63ukjbki">
                  {group.rows.map(r => <MeterRow key={r.who} who={r.who} score={r.score} brand={r.brand} data-id="element-10295" data-mp="2be82mvre3rks" />)}
                </div>
              </div>)}
          </div>

          {/* B · Ontology — categories on the left, real integrations right */}
          <div className="flex flex-col gap-4" data-id="element-10296" data-mp="2l9ru95yx843q">
            <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-ink-faint" data-id="element-10297" data-mp="2gci2hjo27gcq">Marketing tools by category</span>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-10298" data-mp="2u2lihswcidtq">

              {/* Left · category filters (fillers — not wired to the grid) */}
              <div className="lg:col-span-1 flex flex-col gap-2 lg:max-h-[600px] lg:overflow-y-auto lg:pr-2" data-id="element-10299" data-mp="341u8ybs05abc">
                <span className="inline-flex items-center gap-2 rounded-lg border border-brand bg-white py-2 px-3 text-[13px] font-semibold text-brand" data-id="element-10300" data-mp="2z39hp1uqa0z1">
                  <Filter className="w-4 h-4" data-id="element-10301" data-mp="3ipay77t66r03" />All Categories
                </span>
                {MARKETING_CATEGORIES.map(({
                name,
                icon: Icon
              }) => <span key={name} className="inline-flex items-center gap-2 rounded-lg border border-sand-200 bg-white py-2 px-3 text-[13px] font-medium text-ink hover:border-sand-300 transition-colors cursor-default" data-id="element-10302" data-mp="38pk7lh7qcw5a">
                    <Icon className="w-4 h-4 text-ink-faint flex-none" data-id="element-10303" data-mp="2zvyqhr83f2sp" />{name}
                  </span>)}
              </div>

              {/* Right · real integrations from the catalog (3 columns) */}
              <div className="lg:col-span-3" data-id="element-10304" data-mp="2pxrc9g56uyue">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 lg:max-h-[600px] lg:overflow-y-auto lg:pr-1" data-id="element-10305" data-mp="2uy5lh1ij0eu0">
                  {shown.length === 0 ? Array.from({
                  length: 12
                }).map((_, i) => <div key={i} className="h-[58px] rounded-xl bg-cream border border-sand-200 animate-pulse" data-id="element-10306" data-mp="2kzpucyji15ki" data-mpi={i} />) : shown.map(item => <IntegrationTile key={item.slug} item={item} available={availableInChatGPT(item)} data-id="element-10307" data-mp="180avetkbzome" />)}
                </div>
              </div>

            </div>
          </div>

        </section>

        {/* ── Never hit a dead end — plain background ────────────── */}
        <section className="flex flex-col gap-8 md:gap-12 py-4 md:py-10" data-id="element-10308" data-mp="1mwadmx15n559">
          <SectionIntro className="mx-auto flex max-w-[58ch] flex-col items-center gap-3 text-center" data-id="element-10309" data-mp="1qxltiau88c09">
            <h2 className="font-title text-3xl font-bold leading-[1.05] tracking-[-0.03em] md:text-[42px]" data-id="element-10310" data-mp="1vwf866l92920">Never hit a dead end<span className="text-brand" data-id="element-10311" data-mp="20mvopzkdaasl">.</span></h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-10312" data-mp="20pi4gmhevwlw">
              No integration yet? If a tool isn't in Giga's directory, it finds it on the web and
              builds a connection on the spot.
            </p>
          </SectionIntro>

          {/* Ask → Giga runs the checks & builds it → connect link */}
          <div className="flex flex-col items-center w-full" data-id="element-10313" data-mp="3037gkqucd07">
            {/* The ask */}
            <div className="w-full max-w-[700px]" data-id="element-10314" data-mp="3v342qyi88vvv">
              <ChatBox send data-id="element-10315" data-mp="3v2ca9wjxfe9t">
                <span className="inline-flex items-center gap-1.5 font-semibold text-brand-dark align-[-4px] mr-1.5" data-id="element-10316" data-mp="3q4iz0p3rnu2o">
                  <img src={GIGA_MARK} alt="" className="w-[18px] h-[18px] rounded-[4px]" data-id="element-10317" data-mp="3l5zij47xzahp" />@Giga
                </span>
                can we connect Larkspur?
              </ChatBox>
            </div>

            <FlowLink data-id="element-10318" data-mp="hoh7knwvhnd2" />

            {/* Giga runs the checks and builds it */}
            <div className="w-full max-w-[560px] relative rounded-2xl border-2 border-dashed border-[#D6CCC0] bg-sand-50 pt-7 px-4 md:px-5 pb-4 flex flex-col gap-2" data-id="element-10319" data-mp="cxr2lbsczmiw">
              <div className="absolute -top-3 left-5 flex items-center gap-2 bg-ink text-white rounded-full py-1.5 pl-3 pr-4" data-id="element-10320" data-mp="7wtmr5utn4kk">
                <Sparkles className="w-3.5 h-3.5 text-brand-lighter" data-id="element-10321" data-mp="10nlcbkhdsuyx" />
                <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase" data-id="element-10322" data-mp="10lifaghta1kp">Giga builds it</span>
              </div>
              {DEADEND_CHECKS.map(({
              icon: Icon,
              label,
              status,
              tone
            }) => <div key={label} className="bg-white border border-sand-200 rounded-xl p-3 flex items-center gap-2.5" data-id="element-10323" data-mp="2l4krbc5irb8z">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-brand/10 text-brand-dark flex-none" data-id="element-10324" data-mp="2q2w3vun77zuw"><Icon className="w-4 h-4" data-id="element-10325" data-mp="2b9kl4smuk58t" /></span>
                  <span className="text-[13px] text-ink flex-1 min-w-0" data-id="element-10326" data-mp="2g6t0klgm1v6d">{label}</span>
                  {tone === 'green' ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 flex-none" data-id="element-10327" data-mp="227tdvjcnrqcu"><Check className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-10328" data-mp="276wbwju80wbb" />{status}</span> : <span className="text-[11px] text-ink-faint flex-none" data-id="element-10329" data-mp="foghsce40uq">{status}</span>}
                </div>)}
            </div>

            <FlowLink data-id="element-10330" data-mp="5dgijf6r13cn" />

            {/* Giga's reply with the connect link */}
            <div className="w-full max-w-[700px]" data-id="element-10331" data-mp="fhwwu44kycec">
              <ChatBox data-id="element-10332" data-mp="fgc1icafypff">
                <div className="flex items-start gap-2.5" data-id="element-10333" data-mp="ajbxe6x5jwqy">
                  <img src={GIGA_MARK} alt="" className="w-[18px] h-[18px] rounded-[4px] flex-none mt-0.5" data-id="element-10334" data-mp="akwr5ubn7bnr" />
                  <div className="flex flex-col gap-2.5" data-id="element-10335" data-mp="oc1j0a8rqznj">
                    <span data-id="element-10336" data-mp="jjrtgvbkg1ls">Done — I've built the <strong className="font-semibold" data-id="element-10337" data-mp="jigur9wz9k1j">Larkspur</strong> integration. Click to connect it:</span>
                    <a href="#" className="self-start inline-flex items-center gap-1.5 bg-brand hover:bg-brand-dark text-white rounded-full px-4 py-2 text-[13px] font-bold transition-colors" data-id="element-10338" data-mp="y739paip09s8">
                      Connect Larkspur <ArrowRight className="w-3.5 h-3.5" data-id="element-10339" data-mp="177i2sw1wet6a" />
                    </a>
                  </div>
                </div>
              </ChatBox>
            </div>
          </div>

        </section>

        {/* ── Secure vault — its own panel ───────────────────────── */}
        <section className={`${CARD} gap-8 md:gap-10`} data-id="element-10340" data-mp="350ynfqov6aid">
          <SectionIntro className="mx-auto flex max-w-[58ch] flex-col items-center gap-3 text-center" data-id="element-10341" data-mp="36h2mqnwnqwcz">
            <h2 className="font-title text-3xl font-bold leading-[1.05] tracking-[-0.03em] md:text-[42px]" data-id="element-10342" data-mp="3bf4eryc2a60l">Keep private data out of the conversation<span className="text-brand" data-id="element-10343" data-mp="3fhr0dhi52upc">.</span></h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-10344" data-mp="3kg2fa6najvef">
              API keys, OAuth tokens, and database credentials stay securely in Giga's encrypted
              vault. ChatGPT gets the access it needs—without ever seeing your secrets.
            </p>
          </SectionIntro>

          {/* Prompt → vault → answer, one centred column */}
          <div className="flex flex-col items-center w-full" data-id="element-10345" data-mp="31hg9wfkqri4p">
            <div className="w-full max-w-[700px]" data-id="element-10346" data-mp="31j13a1vbkv0z">
              <ChatBox send data-id="element-10347" data-mp="242c3edpxk2u8">
                <span className="inline-flex items-center gap-1.5 font-semibold text-brand-dark align-[-4px] mr-1.5" data-id="element-10348" data-mp="2410zmdwhiv34">
                  <img src={GIGA_MARK} alt="" className="w-[18px] h-[18px] rounded-[4px]" data-id="element-10349" data-mp="28zkn4iqq8exl" />@Giga
                </span>
                what were our metrics last week?
              </ChatBox>
            </div>

            <FlowLink data-id="element-10350" data-mp="1wjcr0s0vzbkd" />

            <div className="w-full max-w-[560px] flex flex-col gap-2.5" data-id="element-10351" data-mp="1rjghkd9f6rxf">
              <div className="relative rounded-2xl border-2 border-dashed border-[#D6CCC0] bg-sand-50 pt-7 px-4 md:px-5 pb-4 flex flex-col gap-2" data-id="element-10352" data-mp="1rhne16u3sntn">
                <div className="absolute -top-3 left-5 flex items-center gap-2 bg-ink text-white rounded-full py-1.5 pl-3 pr-4" data-id="element-10353" data-mp="1ija6ey17241f">
                  <ShieldCheck className="w-3.5 h-3.5 text-brand-lighter" data-id="element-10354" data-mp="1dra5p3dav73s" />
                  <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase" data-id="element-10355" data-mp="18s7b6cuy4ja2">Giga Vault</span>
                </div>
                <div className="bg-white border border-sand-200 rounded-xl p-3 flex items-center gap-x-2.5 gap-y-1.5 flex-wrap" data-id="element-10356" data-mp="13vp5narskgwo">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-brand/10 text-brand-dark flex-none" data-id="element-10357" data-mp="ut7hurwhng7k"><Database className="w-4 h-4" data-id="element-10358" data-mp="l52aj87fbb1z" /></span>
                  <div className="flex flex-col min-w-0" data-id="element-10359" data-mp="l4ag40pkbjda">
                    <span className="text-[12.5px] font-semibold text-ink leading-tight" data-id="element-10360" data-mp="p7fcdlj1uoth">Database credentials</span>
                    <span className="font-mono text-[11px] text-ink-faint tracking-wider truncate" data-id="element-10361" data-mp="b8pfo7056r5n">postgres://••••••••</span>
                  </div>
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-brand-dark" data-id="element-10362" data-mp="13w35ll5famg9"><ShieldCheck className="w-3.5 h-3.5" data-id="element-10363" data-mp="18v6bt8x52ypt" />Secured by Giga</span>
                </div>
                <div className="bg-white border border-sand-200 rounded-xl p-3 flex items-center gap-x-2.5 gap-y-1.5 flex-wrap" data-id="element-10364" data-mp="u69uel9709cj">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-brand/10 text-brand-dark flex-none" data-id="element-10365" data-mp="z4l4mx3eglva"><KeyRound className="w-4 h-4" data-id="element-10366" data-mp="1mu5p5kf7br6o" /></span>
                  <div className="flex flex-col min-w-0" data-id="element-10367" data-mp="1rlntmigfo9we">
                    <span className="text-[12.5px] font-semibold text-ink leading-tight" data-id="element-10368" data-mp="1rn8ok96ntuus">API token</span>
                    <span className="font-mono text-[11px] text-ink-faint tracking-wider truncate" data-id="element-10369" data-mp="3ca8d37mfwxtz">Bearer sk_live_••••</span>
                  </div>
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-brand-dark" data-id="element-10370" data-mp="3l4we2lkzftcz"><ShieldCheck className="w-3.5 h-3.5" data-id="element-10371" data-mp="3h29kaf4r3yd5" />Secured by Giga</span>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 text-[12px] text-ink-muted" data-id="element-10372" data-mp="2te8gfjiip642"><Check className="w-3.5 h-3.5 text-emerald-600 flex-none" strokeWidth={2.5} data-id="element-10373" data-mp="2oenh37zfqvqt" /><span data-id="element-10374" data-mp="2ogglshqhn5w6"><strong className="text-ink font-medium" data-id="element-10375" data-mp="32dvk6bypyo0a">MCP &amp; OAuth tools</strong> skip the vault — already secure.</span></span>
            </div>

            <FlowLink data-id="element-10376" data-mp="2xe0ugbqok4gt" />

            <div className="w-full max-w-[700px]" data-id="element-10377" data-mp="2xgwpnsb58f1k">
              <ChatBox data-id="element-10378" data-mp="hmv0rwwqxqff">
                <span className="inline-flex items-center gap-2" data-id="element-10379" data-mp="hm34skz8lqom">
                  <img src={GIGA_MARK} alt="" className="w-[18px] h-[18px] rounded-[4px] flex-none" data-id="element-10380" data-mp="cp328k1lj629" />
                  <span data-id="element-10381" data-mp="wrh19mi2vtrt">Last week: <strong className="font-semibold" data-id="element-10382" data-mp="wrqvjma5on3b">1,240 signups · $48k revenue · 3.4× ROAS</strong>.</span>
                </span>
              </ChatBox>
            </div>
          </div>
        </section>

        {/* ── More than answering questions — analyze→execute, tabbed on scroll ── */}
        <section className="py-2 md:py-8" data-id="element-10383" data-mp="1aj498jlduqqf">
          <MoreThanAnswers data-id="element-10384" data-mp="1ak704ripgt6c" />
        </section>

        {/* ── Embedded expertise ────────────────────────────────── */}
        <section className={`${CARD} gap-8 md:gap-10`} data-id="element-10385" data-mp="1kga3g0pamxuv">
          <SectionIntro className="mx-auto flex max-w-[56ch] flex-col items-center gap-3 text-center" data-id="element-10386" data-mp="1kep663blh0tb">
            <h2 className="font-title text-3xl font-bold leading-[1.05] tracking-[-0.03em] md:text-[42px]" data-id="element-10387" data-mp="1pg4qtu9v15b6">Embedded expertise<span className="text-brand" data-id="element-10388" data-mp="1teuc09153r5d">.</span></h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-10389" data-mp="3r7hr260vup0w">
              Giga comes pre-packed with the full landscape of approaches, strategies, skills, and
              tools — a whole library you can put to work.
            </p>
          </SectionIntro>

          {/* Two ChatGPT windows: plain question vs @Giga */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 items-start" data-id="element-10390" data-mp="3iw92emq014ur">
            {/* ── ChatGPT ── */}
            <div className="flex flex-col gap-3" data-id="element-10391" data-mp="39xnpp877kk1w">
              <div className="flex items-center gap-2" data-id="element-10392" data-mp="39yypyy1g39o9">
                <img src={OPENAI_ICON} alt="" className="w-4 h-4" data-id="element-10393" data-mp="aisdlcr89i3y" />
                <span className="font-mono text-[11px] tracking-[0.12em] uppercase text-ink-faint" data-id="element-10394" data-mp="afwfnofdqtfi">ChatGPT</span>
              </div>
              <div className="bg-white border border-sand-200 rounded-[22px] shadow-[0_4px_20px_rgba(43,33,26,0.06)] py-3 pl-4 pr-2.5 flex items-center gap-2.5" data-id="element-10395" data-mp="1h2km2hp6b42">
                <span className="flex-1 text-[14.5px] text-ink" data-id="element-10396" data-mp="3sq81tdyouaj0">What is UGC marketing?</span>
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0D0D0D] text-white flex-none" data-id="element-10397" data-mp="2m5qdvh3rds0s"><ArrowUp className="w-3.5 h-3.5" data-id="element-10398" data-mp="1hk8bhhrn9ogp" /></span>
              </div>
              <div className="bg-white border border-sand-200 rounded-2xl p-5 flex flex-col gap-3 text-[14px] leading-[1.6] text-ink" data-id="element-10399" data-mp="1hljhzpch5abi">
                <p data-id="element-10400" data-mp="18rojv19rsk5d"><strong className="font-semibold" data-id="element-10401" data-mp="18rohwvq7ma2q">User-generated content (UGC) marketing</strong> is a strategy where brands use content made by their customers or unpaid creators — reviews, photos, videos, and social posts — instead of producing it themselves.</p>
                <p data-id="element-10402" data-mp="20h01sb5lt3u9">It's generally seen as more authentic and trustworthy than brand-made content, and it's widely used across e-commerce, social media, and paid advertising.</p>
                <p className="text-ink-muted" data-id="element-10403" data-mp="1rguue10eu5g3">Would you like some examples or best practices to get started?</p>
              </div>
            </div>

            {/* ── Giga ── */}
            <div className="flex flex-col gap-3" data-id="element-10404" data-mp="g4aimqz1h2fg">
              <div className="flex items-center gap-2" data-id="element-10405" data-mp="g0wi7o9xr3an">
                <img src={GIGA_MARK} alt="" className="w-4 h-4 rounded-[3px]" data-id="element-10406" data-mp="b3ehgl62nudq" />
                <span className="font-mono text-[11px] tracking-[0.12em] uppercase text-brand-dark" data-id="element-10407" data-mp="110rmbtqw6en">Giga in ChatGPT</span>
              </div>
              <div className="bg-white border border-sand-200 rounded-[22px] shadow-[0_4px_20px_rgba(43,33,26,0.06)] py-3 pl-4 pr-2.5 flex items-center gap-2.5" data-id="element-10408" data-mp="5zlqo7x9gk1i">
                <span className="flex-1 text-[14.5px] text-ink" data-id="element-10409" data-mp="fq6zeo6nf4y5"><span className="font-semibold text-brand-dark" data-id="element-10410" data-mp="fr884m06zfcp">@Giga</span> what is UGC marketing?</span>
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0D0D0D] text-white flex-none" data-id="element-10411" data-mp="jt391pe7nqpy"><ArrowUp className="w-3.5 h-3.5" data-id="element-10412" data-mp="towtduy68oia" /></span>
              </div>
              <div className="bg-white border border-brand/30 ring-1 ring-brand/10 rounded-2xl p-5 flex flex-col gap-3 text-[14px] leading-[1.6] text-ink" data-id="element-10413" data-mp="tmtu0kb4pj0w">
                <GigaWorking data-id="element-10414" data-mp="2vhba4qozbmx4" />
                <p data-id="element-10415" data-mp="2vi4935hp584p">UGC is content made by your <strong className="font-semibold" data-id="element-10416" data-mp="30fcvxjdsi0ca">customers and creators</strong> — not your brand. It works best for DTC, apps, and social-first teams.</p>
                <p data-id="element-10417" data-mp="2sf6swobarstd">Here's everything you'd need to run it:</p>
                <p data-id="element-10418" data-mp="2ng3lwybrbcir">📖 <a href="#" className="text-brand-dark font-semibold underline underline-offset-2 hover:text-brand" data-id="element-10419" data-mp="2ngdfswzxa3tq">The Giga Guide to UGC Marketing</a> — the canonical playbook (12 min read).</p>
                <p data-id="element-10420" data-mp="2xd8a13p5xi27"><strong className="font-semibold" data-id="element-10421" data-mp="2xdi3j19f27cn">42 tools</strong> across 6 subcategories — creator sourcing, editing &amp; captions, rights &amp; contracts, whitelisting, hooks &amp; scripts, and analytics. Want me to start with one?</p>
                <div className="flex flex-col gap-1.5" data-id="element-10422" data-mp="36e67f353irbt">
                  <p data-id="element-10423" data-mp="3l3lv2a9bygna"><strong className="font-semibold" data-id="element-10424" data-mp="3l3luo95f4emr">Skills you can install:</strong></p>
                  <ul className="flex flex-col gap-1" data-id="element-10425" data-mp="3g3pr2b0mup7q">
                    {UGC_SKILLS.map(sk => <li key={sk.name} className="flex gap-2" data-id="element-10426" data-mp="3u40hxe1y03sw"><span className="text-brand-dark leading-6" data-id="element-10427" data-mp="3u527enjpgsu5">•</span><span data-id="element-10428" data-mp="3u1o1j5bu21hy"><strong className="font-semibold" data-id="element-10429" data-mp="3q2fa4749mfpk">{sk.name}</strong> — {sk.skill}</span></li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Put it down anywhere — plain background ────────────── */}
        <section className="flex flex-col gap-8 md:gap-12 py-4 md:py-10" data-id="element-10430" data-mp="hibvfe4w2da3">
          <SectionIntro className="mx-auto flex max-w-[64ch] flex-col items-center gap-3 text-center" data-id="element-10431" data-mp="3k56inhx3nmk">
            <h2 className="font-title text-3xl font-bold leading-[1.08] tracking-[-0.03em] md:text-[44px]" data-id="element-10432" data-mp="8i6pz9j99oyk">Put it down in ChatGPT, pick it up in <RotatingWord data-id="element-10433" data-mp="3uqy6xp4vziq1" /></h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-10434" data-mp="3ur7wxcpxlpvq">
              Giga is its own system — it lives outside any single provider. Your credentials, skills,
              and files come with you, so you can work from anywhere and you're never locked in.
            </p>
          </SectionIntro>

          {/* Providers → one shared Giga system */}
          <div className="flex flex-col items-stretch max-w-[760px] mx-auto w-full" data-id="element-10435" data-mp="3c0zznb8wxtng">
            <div className="grid grid-cols-3 gap-3 md:gap-6" data-id="element-10436" data-mp="i8h1w3z5c351">
              {PROVIDERS.map(p => <div key={p.name} className="flex flex-col items-center min-w-0" data-id="element-10437" data-mp="iabfsyc02uzm">
                  <div className="w-full bg-white border border-sand-200 rounded-2xl shadow-sm p-3 md:p-4 flex items-center justify-center gap-2 md:gap-2.5" data-id="element-10438" data-mp="e7osnaq9xy8r">
                    <img src={p.logo} alt="" className="w-5 h-5 md:w-6 md:h-6 flex-none" data-id="element-10439" data-mp="4hvj0f9ozt75" />
                    <span className="font-title font-bold text-[13px] md:text-[16px] truncate" data-id="element-10440" data-mp="3vnwpsbmmk0mz">{p.name}</span>
                  </div>
                  <div className="w-0 h-8 md:h-10 border-l-2 border-dashed border-[#D6CCC0]" data-id="element-10441" data-mp="3qmqt62puft86" />
                </div>)}
            </div>

            <div className="relative border-2 border-dashed border-[#D6CCC0] rounded-[28px] bg-sand-50 pt-11 px-4 md:px-8 pb-6 md:pb-8" data-id="element-10442" data-mp="3mojlryh9s49n">
              <div className="absolute -top-3.5 left-6 md:left-8 flex items-center gap-2 bg-ink text-white rounded-full py-1.5 pl-3 pr-4" data-id="element-10443" data-mp="1kvlq7uw3yxl0">
                <Sparkles className="w-3.5 h-3.5 text-brand-lighter" data-id="element-10444" data-mp="1fx0lbi5wvpq8" />
                <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase" data-id="element-10445" data-mp="1neqe1jounbcz">Giga · your company brain</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4" data-id="element-10446" data-mp="1scib0s4cpno1">
                {SYSTEM_BOXES.map(({
                icon: Icon,
                name,
                desc
              }) => <div key={name} className="bg-white border border-sand-200 rounded-2xl p-4 flex flex-col gap-2" data-id="element-10447" data-mp="226h7bahqud82">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-brand/10 text-brand-dark" data-id="element-10448" data-mp="19fz2a3xgm1s0"><Icon className="w-5 h-5" data-id="element-10449" data-mp="1efbpwqw5ncvn" /></span>
                    <span className="font-title font-bold text-[15px]" data-id="element-10450" data-mp="1edsaf2ibp5ux">{name}</span>
                    <span className="text-[12.5px] leading-[1.45] text-ink-muted" data-id="element-10451" data-mp="1ihghlk31lzfn">{desc}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* ── Built for teams ───────────────────────────────────── */}
        <section className={`${CARD} gap-8 md:gap-10`} data-id="element-10452" data-mp="uqu10gsdtkm4">
          <SectionIntro className="mx-auto flex max-w-[60ch] flex-col items-center gap-3 text-center" data-id="element-10453" data-mp="up96us9zc3os">
            <h2 className="font-title text-3xl font-bold leading-[1.05] tracking-[-0.03em] md:text-[42px]" data-id="element-10454" data-mp="prpopha5v6rk">Built for teams<span className="text-brand" data-id="element-10455" data-mp="13po1ii8u2v4y">.</span></h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-10456" data-mp="ytqpup8imcyi">
              Connections, files, skills — share any of it exactly where it belongs: a whole group,
              a single project space, or one person. No re-entering keys, no shared passwords.
            </p>
          </SectionIntro>

          {/* Sharing visual — a credential / file / skill, shared where it belongs */}
          <TeamSharingVisual data-id="element-10457" data-mp="g0mrwro9ldy3" />
        </section>

        {/* ── Get started — auto-playing install walkthrough ────── */}
        {/* On ink, like the closing panel on the home page, the pricing page
         and every feature page. The page previously ended on the same cream it
         opened with and a small pill floating in it, so it trailed off rather
         than closing. */}
        {/* Ink, matching every other closing panel on the site. Deliberately
         NO scrub plate here: this panel already carries the animated install
         walkthrough, and a second moving image behind it would compete with
         the one thing the reader is meant to watch. */}
        <section className="relative isolate mt-2 flex flex-col items-center gap-8 overflow-hidden rounded-[22px] bg-[#1A120C] px-6 py-14 text-white md:gap-10 md:px-12 md:py-20" data-id="element-10458" data-mp="2iol9apds61a1">
          <div aria-hidden="true" className="dot-matrix-dark pointer-events-none absolute inset-0 opacity-40" data-id="element-10459" data-mp="28qxddw4funqa" />
          <div className="relative flex max-w-[56ch] flex-col items-center gap-3 text-center" data-id="element-10460" data-mp="2doyzyr12p56i">
            <h2 className="font-title text-3xl font-bold leading-[1.05] tracking-[-0.03em] text-[#FAF8F4] md:text-[42px]" data-id="element-10461" data-mp="xayz9duelbi7">Get started in 30 seconds<span className="text-[#FB923C]" data-id="element-10462" data-mp="12c68lhc72iqz">.</span></h2>
            <p className="text-pretty text-base leading-relaxed text-white/60 md:text-lg" data-id="element-10463" data-mp="1typihrp4l4l2">Installing Giga is as simple as going to plugins.</p>
          </div>
          <div className="relative w-full" data-id="element-10464" data-mp="1p1648rsfgrt4">
            <GetStartedFlow data-id="element-10465" data-mp="1jzgurgkusm75" />
          </div>
          <a href="https://chatgpt.com/plugins?q=giga" target="_blank" rel="noopener noreferrer" className="relative inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-[15px] font-bold text-[#2B211A] transition-[background-color,transform] duration-200 ease-out hover:-translate-y-px hover:bg-[#F5EFE7]" data-id="element-10466" data-mp="1k08muhf8s1so">
            Get started <ArrowRight className="w-4 h-4" data-id="element-10467" data-mp="22ykxy8pprlqw" />
          </a>
          <p className="relative text-[13px] text-white/40" data-id="element-10468" data-mp="22x1igkbvteq6">$100 in credits free · No card required</p>
        </section>

      </div>
    </div>;
}